import { cn, Dropdown, Tooltip } from '@magicdoorinc/ui';
import { createMemo, Show, For } from 'solid-js';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { SubMenuItem } from '~/components/layouts/Menu/SubMenuItems';
import { MainMenuButton } from './MainMenuButton';
import type { MainMenuItemProps } from '~/components/layouts/Menu/types/menuItem';

export const MainMenuItem = (props: MainMenuItemProps) => {
  const menu = useMenu();

  const isDesktop = createMemo(() => !menu?.isMobile());

  const hasSubMenus = createMemo(() => props.item.subMenuItems && props.item.subMenuItems.length > 0);
  const disableTooltip = createMemo(() => {
    return (!menu?.isManuallyCollapsed() && !menu?.isCollapsed()) || menu?.isActive(props.item);
  });

  const shouldUsePopover = createMemo(() => {
    return menu?.isManuallyCollapsed() && isDesktop() && hasSubMenus();
  });

  const clickHandler = () => {
    const isCurrentlyActive = menu?.isActive(props.item);

    if (isCurrentlyActive) {
      menu?.setActiveMenuItem(undefined);
    } else {
      menu?.setActiveMenuItem(props.item);
    }
    if (!hasSubMenus() && !isDesktop()) {
      props.setIsSidebarOpen(false);
    }
  };

  const menuItem = (
    <li
      id={menu?.getMenuItemId(props.item)}
      class={cn(
        'group relative cursor-pointer rounded-lg border-2 border-transparent',
        { 'bg-[var(--menu-panel-color)]': menu?.isActive(props.item) },
        { 'bg-[var(--menu-selected-color)]': menu?.isSelected(props.item) && !menu?.isActive(props.item) },
        { 'mr-0 rounded-r-none': menu?.isActive(props.item) && !menu?.isSelected(props.item) && !menu?.isManuallyCollapsed() },
        {
          'hover-allowed:hover:border-[var(--menu-selected-color)]': !menu?.isSelected(props.item) && !menu?.isActive(props.item),
        },
        { 'mr-2': menu?.isManuallyCollapsed() },
        {
          'hover-allowed:hover:mr-2': menu?.isCollapsed() && !menu?.isActive(props.item),
        },
        {
          'mr-2': menu?.isSelected(props.item) && menu?.isCollapsed(),
        },
        {
          'mr-0 rounded-r-none':
            menu?.isActive(props.item) && menu?.isSelected(props.item) && menu?.isCollapsed() && !menu?.isManuallyCollapsed(),
        },
        { 'rounded-lg': menu?.isManuallyCollapsed() && menu?.isActive(props.item) }
      )}
      onClick={clickHandler}>
      <span class={cn('flex w-fit items-center p-3 text-left')}>
        <div class="relative z-10 inline-block">
          <MainMenuButton item={props.item} setIsSidebarOpen={props.setIsSidebarOpen} isMenuCollapsed={props.isMenuCollapsed} />
        </div>
      </span>
      <div
        class="pointer-events-none absolute bottom-0 left-0 h-px w-full"
        classList={{
          'opacity-100': menu?.isSelected(props.item) || menu?.isActive(props.item),
          'opacity-0': !menu?.isSelected(props.item) && !menu?.isActive(props.item),
        }}
        style={{
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0) 100%)',
        }}
      />
    </li>
  );

  return (
    <Show
      when={shouldUsePopover()}
      fallback={
        <Tooltip class="w-full" bgColor={props.tooltipBgColor} message={props.item.text} align="right" disabled={disableTooltip()}>
          {menuItem}
        </Tooltip>
      }>
      <Dropdown
        triggerElement={
          <Tooltip class="w-full" bgColor={props.tooltipBgColor} message={props.item.text} align="right" disabled={disableTooltip()}>
            {menuItem}
          </Tooltip>
        }
        bgColor={props.popoverColor}
        contentClass="absolute -top-16 left-16 w-64 gap-2 rounded-lg pr-2 text-white">
        {(_isOpen, open) => (
          <>
            <div class="mb-2 border-b border-white/20 px-4 pb-3 pt-1 font-semibold text-white/70">{props.item.text}</div>
            <ul style={{ '--menu-active-color': props.activeColor }}>
              <For each={props.item.subMenuItems}>
                {(subItem) => (
                  <SubMenuItem
                    item={subItem}
                    setIsSidebarOpen={props.setIsSidebarOpen}
                    onNavigate={(e) => {
                      e.stopPropagation();
                      menu?.setActiveMenuItem(subItem);
                      open(false);
                    }}
                  />
                )}
              </For>
            </ul>
          </>
        )}
      </Dropdown>
    </Show>
  );
};
